import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IExportRedemptionsParams } from '@feathr/blackbox';
import { EGrowthCreditEntryClass } from '@feathr/blackbox';
import {
  Button,
  DateRangeSelects,
  Fieldset,
  Input,
  Modal,
  UniversalSelect,
} from '@feathr/components';
import type { IDateRangeOption } from '@feathr/extender/hooks';
import { useExportModal } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';

interface ITypeOption {
  id: string;
  name: string;
}

interface IProps {
  opened: boolean;
  onClose: () => void;
}

function ExportRedemptionsModal({ opened, onClose }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { GrowthCreditEntries } = useStore();

  const {
    email,
    emailHadFocus,
    customDateRange,
    selectedDateRange,
    isLoading,
    isSubmitDisabled,
    validationErrors,
    dateRangeOptions,
    handleEmailBlur,
    handleChangeEmail,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExport,
    resetFormState,
    resolveDateRange,
  } = useExportModal(t);

  const [typeFilter, setTypeFilter] = React.useState<ITypeOption | undefined>(undefined);

  const typeOptions: ITypeOption[] = [
    { id: 'all', name: t('All types') },
    { id: EGrowthCreditEntryClass.MediaRedemption, name: t('Media Balance') },
    { id: EGrowthCreditEntryClass.InvoiceRedemption, name: t('Invoice Payment') },
  ];

  function handleTypeChange(option: ITypeOption | null): void {
    setTypeFilter(option ?? undefined);
  }

  function handleClose(): void {
    resetFormState();
    setTypeFilter(undefined);
    onClose();
  }

  async function handleConfirm(): Promise<void> {
    const { startDate, endDate } = resolveDateRange();

    const params: IExportRedemptionsParams = {
      email,
      start_date: startDate,
      end_date: endDate,
    };

    if (typeFilter && typeFilter.id !== 'all') {
      if (typeFilter.id === EGrowthCreditEntryClass.MediaRedemption) {
        params.redemption_type = 'media_balance';
      } else if (typeFilter.id === EGrowthCreditEntryClass.InvoiceRedemption) {
        params.redemption_type = 'invoice_payment';
      }
    }

    await handleExport(() => GrowthCreditEntries.exportRedemptions(params), handleClose, t);
  }

  return (
    <Modal
      allowOverflow
      description={t(
        'Customize your export settings below. Your CSV file will be delivered via email with a secure download link since exporting may take a few minutes to process.',
      )}
      onClose={handleClose}
      opened={opened}
      rightActions={[
        <Button disabled={isLoading} key={'cancel'} onClick={handleClose}>
          {t('Cancel')}
        </Button>,
        <Button
          disabled={isSubmitDisabled}
          isLoading={isLoading}
          key={'confirm'}
          onClick={handleConfirm}
          type={'primary'}
        >
          {t('Send Export to Email')}
        </Button>,
      ]}
      title={t('Export redemption data')}
    >
      <Fieldset>
        <Input
          label={t('Email address')}
          onBlur={handleEmailBlur}
          onChange={handleChangeEmail}
          placeholder={t('firstname.lastname@nonprofit.org')}
          type={'text'}
          validationError={emailHadFocus ? flattenError(validationErrors?.email) : undefined}
          value={email}
        />
        <UniversalSelect<ITypeOption>
          label={t('Redemption type')}
          name={'redemption_type'}
          onSelectSingle={handleTypeChange}
          options={typeOptions}
          value={typeFilter ?? typeOptions[0]}
        />
        <UniversalSelect<IDateRangeOption>
          label={t('Date range')}
          name={'date_range'}
          onSelectSingle={handleDateRangeChange}
          options={dateRangeOptions}
          validationError={flattenError(validationErrors?.dateRange)}
          value={dateRangeOptions.find((opt) => opt.id === selectedDateRange)}
        />
        {selectedDateRange === 'custom' && (
          <DateRangeSelects onChange={handleCustomDateRangeChange} t={t} value={customDateRange} />
        )}
      </Fieldset>
    </Modal>
  );
}

export default observer(ExportRedemptionsModal);
